import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getVisiblePosts } from '../data/blogPosts';

// Robot/AI icon component for anonymous posts
const RobotIcon: React.FC = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        {/* Robot head/body */}
        <rect x="6" y="8" width="12" height="10" rx="2" ry="2" fill="#1e3a8a" opacity="0.9" />

        {/* Antenna */}
        <line x1="12" y1="6" x2="12" y2="4" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="12" cy="3" r="1" fill="#06b6d4" />

        {/* Eyes */}
        <circle cx="9.5" cy="11" r="1.5" fill="white" />
        <circle cx="14.5" cy="11" r="1.5" fill="white" />
        <circle cx="9.5" cy="11" r="0.7" fill="#c084fc" />
        <circle cx="14.5" cy="11" r="0.7" fill="#c084fc" />
        <circle cx="9.5" cy="11" r="0.3" fill="#111" />
        <circle cx="14.5" cy="11" r="0.3" fill="#111" />

        {/* Happy mouth */}
        <path d="M 10 14 Q 12 16 14 14" stroke="#06b6d4" strokeWidth="1.5" strokeLinecap="round" fill="none" />

        {/* Circuit pattern decorations */}
        <line x1="7" y1="19" x2="9" y2="19" stroke="#6366f1" strokeWidth="1" opacity="0.8" />
        <line x1="15" y1="19" x2="17" y2="19" stroke="#6366f1" strokeWidth="1" opacity="0.8" />
        <circle cx="8" cy="19" r="0.5" fill="#3b82f6" opacity="0.7" />
        <circle cx="16" cy="19" r="0.5" fill="#3b82f6" opacity="0.7" />

        {/* Side ports/connectors */}
        <rect x="5.5" y="12" width="1" height="2" rx="0.5" fill="#0ea5e9" opacity="0.8" />
        <rect x="17.5" y="12" width="1" height="2" rx="0.5" fill="#0ea5e9" opacity="0.8" />
    </svg>
);

const AuguryPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const activeCategory = searchParams.get('category') || 'All';
    const [isEmailBannerMinimized, setIsEmailBannerMinimized] = useState(false);

    const visiblePosts = getVisiblePosts();

    // Generate categories dynamically from the available posts
    const categories = ['All', ...Array.from(new Set(visiblePosts.map(post => post.category)))];

    // Sort posts by date (most recent first) and then by ID (highest first) for posts with same date
    const sortedPosts = visiblePosts.sort((a, b) => {
        // Convert date strings to Date objects for comparison
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);

        // First sort by date (newest first)
        const dateComparison = dateB.getTime() - dateA.getTime();

        // If dates are the same, sort by ID (highest first)
        if (dateComparison === 0) {
            return b.id - a.id;
        }

        return dateComparison;
    });

    const filteredPosts = activeCategory === 'All'
        ? sortedPosts
        : sortedPosts.filter(post => post.category === activeCategory);

    const handleCategoryClick = (category: string) => {
        if (category === 'All') {
            setSearchParams({});
        } else {
            setSearchParams({ category });
        }
    };

    const EmptyState: React.FC<{ activeCategory: string }> = ({ activeCategory }) => (
        <div className="col-span-full flex flex-col items-center justify-center py-16 text-center">
            <div className="w-24 h-24 mb-8 rounded-full bg-gray-800/50 flex items-center justify-center">
                <svg
                    className="w-12 h-12 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                    />
                </svg>
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">
                {activeCategory === 'All'
                    ? 'No Auguries Available'
                    : `No ${activeCategory} Articles`
                }
            </h3>
            <p className="text-gray-400 max-w-md mb-8">
                {activeCategory === 'All'
                    ? 'We\'re working on bringing you insightful content. Check back soon for our latest thoughts on technology, markets, and investment strategies.'
                    : `We don't have any articles in the ${activeCategory} category yet. Try browsing other categories or check back later.`
                }
            </p>
            {activeCategory !== 'All' && (
                <button
                    onClick={() => handleCategoryClick('All')}
                    className="text-accent-light hover:text-accent transition-colors font-medium"
                >
                    View All Categories
                </button>
            )}
        </div>
    );

    return (
        <div className="max-w-7xl mx-auto px-4 py-12 md:py-24 relative z-10">
            <div className="text-center mb-16">
                <h1 className="text-4xl md:text-5xl font-bold mb-6 tracking-tighter">
                    <span className="bg-gradient-to-r from-accent to-accent-light bg-clip-text text-transparent" style={{
                        textShadow: '1px 1px 0 #ae4094, -1px -1px 0 #000, 0px 1px 0 #ae4094, 1px 0px 0 #ae4094, 0px -1px 0 #000, -1px 0px 0 #000'
                    }}>
                        Augury
                    </span>
                </h1>
                <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                    Insights from our investment team on market trends, technology shifts, and the future of private equity in the software sector.
                </p>
            </div>

            {/* Category filters */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
                {categories.map(category => (
                    <button
                        key={category}
                        onClick={() => handleCategoryClick(category)}
                        className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${activeCategory === category
                            ? 'bg-accent text-white shadow-lg'
                            : 'bg-gray-800 bg-opacity-50 text-gray-300 hover:bg-gray-700 hover:text-white'
                            }`}
                    >
                        {category}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                {filteredPosts.length === 0 ? (
                    <EmptyState activeCategory={activeCategory} />
                ) : (
                    filteredPosts.map(post => (
                        <article
                            key={post.id}
                            className="bg-gray-900 bg-opacity-50 rounded-lg overflow-hidden transform transition-all hover:scale-102 hover:bg-opacity-70"
                        >
                            <div className="p-8">
                                <h2 className="text-2xl font-bold mb-4 leading-tight">
                                    <a href={`/augury/${post.id}`} className="hover:text-accent-light transition-colors">
                                        {post.title}
                                    </a>
                                </h2>

                                <p className="text-gray-300 mb-6">
                                    {post.excerpt}
                                </p>

                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center mr-3">
                                            {post.author === "Anonymously Generated" ? (
                                                <div className="text-white">
                                                    <RobotIcon />
                                                </div>
                                            ) : (
                                                    <span className="font-bold">{post.author.split(' ').map(n => n[0]).join('')}</span>
                                            )}
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium">{post.author}</div>

                                        </div>
                                    </div>

                                    <a
                                        href={`/augury/${post.id}`}
                                        className="text-accent-light hover:underline text-sm font-medium"
                                    >
                                        Read more →
                                    </a>
                                </div>
                                <div className="flex items-center justify-center text-gray-400 text-xs mt-1">
                                    <span>{post.date}</span>
                                    <span className="mx-2">•</span>
                                    <span>{post.readTime}</span>
                                    <span className="mx-2">•</span>
                                    <span className="text-accent-light">{post.category}</span>
                                </div>
                            </div>
                        </article>
                    ))
                )}
            </div>

            {visiblePosts.length > 0 && (
                <div className="mt-16 text-center">
                    <a
                        href="https://pastasauce.notion.site/2262472aefc58012885deef5fbdc9c6c"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center border border-accent-light text-accent-light hover:bg-accent hover:text-white transition-colors py-3 px-8 rounded-lg font-medium"
                    >
                        <svg
                            className="w-5 h-5 mr-2"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                        </svg>
                        Receive our Augury updates to your email
                    </a>
                </div>
            )}

            {visiblePosts.length > 0 && (
                <div className={`fixed bottom-0 left-0 right-0 z-50 md:hidden transition-transform duration-300 ${isEmailBannerMinimized ? 'translate-y-full' : 'translate-y-0'}`}>
                    <div className="bg-gray-900 bg-opacity-90 border-t border-gray-700/50 p-4 shadow-lg" style={{
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)'
                    }}>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold text-white">Stay Updated</h3>
                            <button
                                onClick={() => setIsEmailBannerMinimized(true)}
                                className="text-gray-400 hover:text-white transition-colors"
                                aria-label="Minimize banner"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <a
                            href="https://pastasauce.notion.site/2262472aefc58012885deef5fbdc9c6c"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center w-full bg-accent bg-opacity-90 text-white py-3 px-4 rounded-lg font-medium hover:bg-accent transition-colors"
                            style={{
                                backdropFilter: 'blur(10px)',
                                WebkitBackdropFilter: 'blur(10px)'
                            }}
                        >
                            <svg
                                className="w-4 h-4 mr-2"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                            </svg>
                            Receive our Augury updates
                        </a>
                    </div>
                </div>
            )}

            {visiblePosts.length > 0 && isEmailBannerMinimized && (
                <div className="fixed bottom-4 right-4 z-50 md:hidden">
                    <button
                        onClick={() => setIsEmailBannerMinimized(false)}
                        className="bg-accent text-white p-3 rounded-full shadow-lg hover:bg-opacity-90 transition-colors"
                        aria-label="Show email subscription"
                    >
                        <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                        </svg>
                    </button>
                </div>
            )}
        </div>
    );
};

export default AuguryPage; 